import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import ReactTooltip from 'react-tooltip';
import {
  Controller,
  ControllerRenderProps,
  FieldValues,
} from 'react-hook-form';
import { Popover } from '@headlessui/react';

import { ReactComponent as InfoIcon } from 'assets/icons/info.svg';
import { ReactComponent as WarningIcon } from 'assets/icons/warning.svg';

interface AssignedSelectSectionItem {
  id: string;
}

interface AssignedSelectSectionProps {
  control: any;
  name: string;
  title: string;
  tooltipId: string;
  tooltipText: string;
  placeholder: string;
  items: AssignedSelectSectionItem[];
  renderItemLabel: (item: any) => ReactNode;
}

const AssignedSelectSection: FC<AssignedSelectSectionProps> = ({
  control,
  name,
  title,
  tooltipId,
  tooltipText,
  placeholder,
  items,
  renderItemLabel,
}) => {
  const selectAllElementRef = useRef<HTMLInputElement | null>(null);

  const [isOverflow, setIsOverflow] = useState(false);

  const dropdownRef = useRef<HTMLButtonElement>(null);

  const selectAllId = `selectAll-${name}`;

  const handleOnChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    selectedId: string,
    { value, onChange }: ControllerRenderProps<FieldValues, string>
  ) => {
    const selectAllElement = selectAllElementRef.current;
    if (!selectAllElement) return;
    if (e.currentTarget.checked) {
      const updatedValue = [...value, selectedId];
      onChange(updatedValue);

      const isAllSelected = updatedValue.length === items.length;
      selectAllElement.indeterminate = !isAllSelected;
      selectAllElement.checked = isAllSelected;
      return;
    }
    const updatedValue = value.filter((id: string) => id !== selectedId);

    const isAllDeselected = updatedValue.length === 0;

    selectAllElement.checked = isAllDeselected;
    selectAllElement.indeterminate = !isAllDeselected;

    onChange(updatedValue);
  };

  const handleOnSelectAll = (
    e: React.ChangeEvent<HTMLInputElement>,
    { onChange }: ControllerRenderProps<FieldValues, string>
  ) => {
    if (e.currentTarget.checked) {
      const updatedValue = items.map((item) => item.id);
      onChange(updatedValue);
      return;
    }
    onChange([]);
  };

  useEffect(() => {
    if (!dropdownRef.current) return;
    const dropdown = dropdownRef.current;
    const position = dropdown.getBoundingClientRect();
    const screenHeight = window.innerHeight;
    const copyPanelHeight = 280;
    const elementHeight = position.top + 26;
    const isOverflow = elementHeight + copyPanelHeight >= screenHeight;
    setIsOverflow(isOverflow);
  }, []);

  return (
    <div className="flex flex-col gap-y-0.8">
      <div className="flex items-center gap-x-1">
        <label className="font-bold text-16 leading-[2.4rem]" htmlFor="">
          {title}
        </label>
        <button data-for={tooltipId} data-tip="">
          <InfoIcon className="w-1.6 h-1.6 all-child:fill-mid-grey" />
        </button>
        <ReactTooltip
          id={tooltipId}
          effect="solid"
          place="bottom"
          className="!bg-white !rounded-[0.8rem] !w-[18.5rem] !h-[auto] z-[9999] flex justify-center items-center !opacity-100 shadow-elevation-07 after:hidden before:hidden !px-[10px] !py-[4px]"
        >
          <span className="text-11 text-darkest-grey flex items-center font-normal">
            {tooltipText}
          </span>
        </ReactTooltip>
      </div>
      <Controller
        control={control}
        name={name}
        rules={{
          validate: (value) => {
            if (value.length === 0) {
              return 'This field is required';
            }
          },
        }}
        render={({ field, fieldState: { error } }) => {
          const { value } = field;
          return (
            <div className="relative">
              <Popover>
                <Popover.Button
                  className={`w-full h-[4.8rem] pl-1.6 cursor-pointer rounded-[1rem] bg-white border-[0.5px] border-light-grey text-left outline-none ${
                    error?.message
                      ? 'shadow-input-error border-[#f44336] hover:border-[#f44336] hover:shadow-input-error focus:border-[#f44336] focus:shadow-input-error'
                      : 'shadow-primary border-lightest-grey hover:border-magenta hover:shadow-input-active focus:border-magenta focus:shadow-input-active'
                  } ${
                    value.length === 0 ? 'text-mid-grey' : 'text-darkest-grey'
                  }`}
                  ref={dropdownRef}
                >
                  {value.length === 0
                    ? placeholder
                    : `${value.length} of ${items?.length} selected`}
                </Popover.Button>
                {error?.message && (
                  <div className={`flex items-center gap-x-1 mt-1`}>
                    <WarningIcon />
                    <span className="text-12 text-red-01">{error.message}</span>
                  </div>
                )}
                <Popover.Panel
                  className={`absolute w-full top-4.8 rounded-[0.8rem] bg-white text-base shadow-primary flex flex-col gap-y-1.6 z-20 p-2 max-h-[28rem] overflow-auto scrollbar ${isOverflow ? '-translate-y-[120%]' : ''}`}
                >
                  <div className="flex gap-x-1.6 items-center ">
                    <input
                      type="checkbox"
                      className="w-1.8 h-1.8"
                      id={selectAllId}
                      ref={selectAllElementRef}
                      onChange={(e) => handleOnSelectAll(e, field)}
                      checked={value?.length === items?.length}
                    />
                    <label
                      className="flex gap-x-1.6 cursor-pointer font-bold"
                      htmlFor={selectAllId}
                    >
                      Select All
                    </label>
                  </div>
                  {items?.map((item) => (
                    <div key={item.id} className="flex gap-x-1.6 items-center">
                      <input
                        type="checkbox"
                        className="w-1.8 h-1.8"
                        id={item.id}
                        onChange={(e) => handleOnChange(e, item.id, field)}
                        checked={value.includes(item.id)}
                      />

                      <label
                        className="flex gap-x-1.6 cursor-pointer"
                        htmlFor={item.id}
                      >
                        {renderItemLabel(item)}
                      </label>
                    </div>
                  ))}
                </Popover.Panel>
              </Popover>
            </div>
          );
        }}
      />
    </div>
  );
};

export default AssignedSelectSection;
